import { Box, Checkbox, Chip, FormControlLabel, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import { Briefcase, Info, MapPin } from 'react-feather';
import { useTranslation } from 'react-i18next';

// Components
import ButtonBookmark from 'src/components/common/ButtonBookmark';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import Point from 'src/components/common/Point';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import { CompanyStatusCode, TypeCompanyProfileType } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import {
  checkCountryHaveState,
  convertToStartCase,
  getColorCompanyStatus,
  getCountryByCountryCode,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';

const StyledBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  position: 'relative',
  background: theme.palette.background.default,
}));

const CompanyProfileCard = ({
  company,
  selected,
  onSelect,
  onChangeCheckbox,
  handleOpenBookmarkNoteForm,
  isBookmark,
  isChecked,
}: {
  company: ICompanyProfile;
  selected?: ICompanyProfile;
  onSelect: () => void;
  onChangeCheckbox: (company: ICompanyProfile, checked: boolean) => void;
  handleOpenBookmarkNoteForm: (companyId: string, companyHasBookmark: boolean) => void;
  isBookmark: boolean;
  isChecked: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isSelected = selected?.id === company.id ? true : false;
  const country = getCountryByCountryCode(company?.country || '');
  const isCountryHaveState = checkCountryHaveState(country?.code);
  const isNonOfficial = (company?.official_tags || []).includes(TypeCompanyProfileType.NON_OFFICIAL);

  const renderCountryInfo = () => {
    if (!country.code) return null;

    return (
      <>
        <FlagCountry code={country.code} />

        {isCountryHaveState ? (
          <Typography variant="caption" color={'text.secondary'}>
            {country.code} {company?.jurisdiction ? `- (${company?.jurisdiction})` : ''}
          </Typography>
        ) : (
          <Typography variant="caption" color={'text.secondary'}>
            ({country.code})
          </Typography>
        )}

        <Point />
      </>
    );
  };

  const renderInformationAvailable = () => {
    const information_available: string[] = [];

    if (company.is_social_media) {
      information_available.push(t('company_profile.available_info.social'));
    }
    if (company.is_stock) {
      information_available.push(t('company_profile.available_info.stock'));
    }
    if (company.is_websites) {
      information_available.push(t('company_profile.available_info.website'));
    }
    if (company.is_contacts) {
      information_available.push(t('company_profile.available_info.contact'));
    }
    if (!isNonOfficial && isEmpty(information_available))
      return <Chip size="small" color="default" label={t('company_profile.available_info.empty_text')} />;

    return getArrayDefault(information_available).map((info) => (
      <Chip key={info} size="small" color="default" label={convertToStartCase(info)} />
    ));
  };

  return (
    <StyledBox
      onClick={onSelect}
      sx={{
        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
        cursor: 'pointer',
      }}
      display={'flex'}
      flexDirection="column"
      gap={1}
      padding={1.5}
      height="100%"
      position={'relative'}
    >
      <Box zIndex={20} right={theme.spacing(1.5)} position={'absolute'}>
        {company?.check_entity_id_type_created_30_days_result && (
          <CheckedCredit
            sx={{
              marginRight: theme.spacing(1),
              verticalAlign:'mi'
            }}
            fontSize="small"
          />
        )}
        <ButtonBookmark
          iconProps={{ sx: { fontSize: 12 } }}
          buttonProps={{
            size: 'medium',
            onClick: (event) => {
              event.stopPropagation();
              handleOpenBookmarkNoteForm(company?.id || '', company?.has_bookmark || false);
            },
          }}
          isMarked={isBookmark}
        />
      </Box>
      <Tooltip placement="left-start" title={company.name}>
        {/* <Typography noWrap pr={1} color={'primary.main'} variant="subtitle1">
              {renderTextOrDefault(company.name)}
            </Typography> */}
        <FormControlLabel
          sx={{ ml: -0.7, maxWidth: 'fit-content' }}
          label={
            <Typography noWrap pr={1} color={'primary.main'} variant="subtitle1">
              {renderTextOrDefault(company.name)}
            </Typography>
          }
          control={
            <Checkbox checked={isChecked} onChange={(_, checked) => onChangeCheckbox(company, checked)} size="small" />
          }
        />
      </Tooltip>

      <Stack alignItems={'center'} direction={'row'} spacing={1}>
        {renderCountryInfo()}

        <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Briefcase />} />

        <Typography display={'flex'} color={'text.secondary'} variant="caption" alignItems="center">
          {renderTextOrDefault(company.company_code)}
        </Typography>

        {company?.status && !isNonOfficial ? (
          <Chip
            label={convertToStartCase(company.status)}
            size="small"
            color={getColorCompanyStatus(company?.status_code ?? CompanyStatusCode.Unknown)}
          />
        ) : (
          <Chip label="Unknown" size="small" color="default" />
        )}
      </Stack>
      <Typography display={'flex'} gap={1} color={'text.secondary'} variant="caption">
        <IconFeather muiProps={{ fontSize: 'small' }} icon={<MapPin />} />
        {renderTextOrDefault(company.full_address).toLocaleString().toUpperCase()}
      </Typography>
      {!isNonOfficial && (
        <Typography alignItems={'center'} display={'flex'} gap={1} color={'text.secondary'} variant="caption">
          <Tooltip placement="top" title={t('company_profile.available_info.tooltip')}>
            <Stack justifyContent="center" alignItems="center">
              <IconFeather muiProps={{ fontSize: 'small' }} icon={<Info />} />
            </Stack>
          </Tooltip>
          {renderInformationAvailable()}
        </Typography>
      )}
    </StyledBox>
  );
};
export default CompanyProfileCard;
